import { createSlice, createAction, PayloadAction } from '@reduxjs/toolkit';
import { globalReset } from '@/store/actions';
import { StudyProgress, UpdateStudyProgressRequest, VocabularyWord } from '@/services/vocabularyService';

// === PROGRESS TRACKING ACTIONS ===
export const updateStudyProgressRequest = createAction<UpdateStudyProgressRequest>(
    'progress/updateStudyProgressRequest',
);
export const updateStudyProgressSuccess = createAction<StudyProgress>('progress/updateStudyProgressSuccess');
export const updateStudyProgressFailure = createAction<string>('progress/updateStudyProgressFailure');

export const fetchStudyProgressRequest = createAction<string>('progress/fetchStudyProgressRequest');
export const fetchStudyProgressSuccess = createAction<StudyProgress>('progress/fetchStudyProgressSuccess');
export const fetchStudyProgressFailure = createAction<string>('progress/fetchStudyProgressFailure');

export const fetchUserStudyProgressRequest = createAction<{
    masteryLevel?: number;
    lastStudiedBefore?: string;
}>('progress/fetchUserStudyProgressRequest');
export const fetchUserStudyProgressSuccess = createAction<StudyProgress[]>('progress/fetchUserStudyProgressSuccess');
export const fetchUserStudyProgressFailure = createAction<string>('progress/fetchUserStudyProgressFailure');

export const fetchWordsForReviewRequest = createAction<number>('progress/fetchWordsForReviewRequest');
export const fetchWordsForReviewSuccess = createAction<VocabularyWord[]>('progress/fetchWordsForReviewSuccess');
export const fetchWordsForReviewFailure = createAction<string>('progress/fetchWordsForReviewFailure');

// === STATE INTERFACE ===
export interface ProgressState {
    studyProgress: StudyProgress[];
    userStudyProgress: StudyProgress[];
    wordsForReview: VocabularyWord[];

    // Loading States
    isLoadingStudyProgress: boolean;
    isUpdatingStudyProgress: boolean;
    isLoadingUserStudyProgress: boolean;
    isLoadingWordsForReview: boolean;

    // Error States
    progressError: string | null;
}

const initialState: ProgressState = {
    studyProgress: [],
    userStudyProgress: [],
    wordsForReview: [],
    isLoadingStudyProgress: false,
    isUpdatingStudyProgress: false,
    isLoadingUserStudyProgress: false,
    isLoadingWordsForReview: false,
    progressError: null,
};

const progressSlice = createSlice({
    name: 'progress',
    initialState,
    reducers: {
        clearProgressError: (state) => {
            state.progressError = null;
        },
        clearWordsForReview: (state) => {
            state.wordsForReview = [];
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(globalReset, () => initialState)

            // === UPDATE STUDY PROGRESS ===
            .addCase(updateStudyProgressRequest, (state) => {
                state.isUpdatingStudyProgress = true;
                state.progressError = null;
            })
            .addCase(updateStudyProgressSuccess, (state, action) => {
                state.isUpdatingStudyProgress = false;
                const index = state.studyProgress.findIndex((progress) => progress.wordId === action.payload.wordId);
                if (index !== -1) {
                    state.studyProgress[index] = action.payload;
                } else {
                    state.studyProgress.push(action.payload);
                }
            })
            .addCase(updateStudyProgressFailure, (state, action) => {
                state.isUpdatingStudyProgress = false;
                state.progressError = action.payload;
            })

            // === FETCH STUDY PROGRESS ===
            .addCase(fetchStudyProgressRequest, (state) => {
                state.isLoadingStudyProgress = true;
                state.progressError = null;
            })
            .addCase(fetchStudyProgressSuccess, (state, action) => {
                state.isLoadingStudyProgress = false;
                const index = state.studyProgress.findIndex((progress) => progress.wordId === action.payload.wordId);
                if (index !== -1) {
                    state.studyProgress[index] = action.payload;
                } else {
                    state.studyProgress.push(action.payload);
                }
            })
            .addCase(fetchStudyProgressFailure, (state, action) => {
                state.isLoadingStudyProgress = false;
                state.progressError = action.payload;
            })

            // === FETCH USER STUDY PROGRESS ===
            .addCase(fetchUserStudyProgressRequest, (state) => {
                state.isLoadingUserStudyProgress = true;
                state.progressError = null;
            })
            .addCase(fetchUserStudyProgressSuccess, (state, action) => {
                state.isLoadingUserStudyProgress = false;
                state.userStudyProgress = action.payload;
            })
            .addCase(fetchUserStudyProgressFailure, (state, action) => {
                state.isLoadingUserStudyProgress = false;
                state.progressError = action.payload;
            })

            // === FETCH WORDS FOR REVIEW ===
            .addCase(fetchWordsForReviewRequest, (state) => {
                state.isLoadingWordsForReview = true;
                state.progressError = null;
            })
            .addCase(fetchWordsForReviewSuccess, (state, action) => {
                state.isLoadingWordsForReview = false;
                state.wordsForReview = action.payload;
            })
            .addCase(fetchWordsForReviewFailure, (state, action) => {
                state.isLoadingWordsForReview = false;
                state.progressError = action.payload;
            });
    },
});

export const { clearProgressError, clearWordsForReview } = progressSlice.actions;

export default progressSlice.reducer;
